import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FileBarChart2, Download, ExternalLink, Clock, Check } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import {
  getClients,
  generateReport,
  getReportHistory,
  getSummary,
  deleteReport,
  getSubscription,
  getReportTemplates,
  purchaseTemplateOrder,
  verifyTemplatePurchase,
  createReportCreditOrder,
  verifyReportCreditPayment,
  getPublicPricing,
  BACKEND_URL,
} from '../utils/api';

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const COLOR_THEME_OPTIONS = [
  { key: 'blue_violet', label: 'Blue & Violet', swatch: '#2563EB' },
  { key: 'emerald_slate', label: 'Emerald', swatch: '#059669' },
  { key: 'amber_rose', label: 'Amber & Rose', swatch: '#F59E0B' },
  { key: 'charcoal_gold', label: 'Charcoal & Gold', swatch: '#CA8A04' },
];
const PLATFORMS = ['all', 'meta', 'google', 'linkedin', 'twitter', 'tiktok'];
const CURRENCIES = [
  { code: 'INR', symbol: '₹' },
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'AED', symbol: 'د.إ' },
  { code: 'SGD', symbol: 'S$' },
];
const CURRENCY_RATES = {
  INR: 1,
  USD: 0.012,
  EUR: 0.011,
  GBP: 0.0095,
  AED: 0.044,
  SGD: 0.016,
};

const convertCurrency = (amount, currencyCode) => {
  const rate = CURRENCY_RATES[currencyCode] || 1;
  return Number(amount || 0) * rate;
};
const getPdfUrl = (pathOrUrl) => {
  if (!pathOrUrl) return '#';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${BACKEND_URL}${pathOrUrl}`;
};

const getCurrencySymbol = (currencyCode) => {
  const found = CURRENCIES.find((c) => c.code === currencyCode);
  return found ? found.symbol : '₹';
};


export default function Reports() {
    const [clients, setClients] = useState([]);
 const [form, setForm] = useState({
   clientId: '',
   dateStart: '',
   dateEnd: '',
   platform: 'all',
   customTitle: '',
   includeComparison: true,
   currency: 'INR',
   templateKey: 'standard',
   colorTheme: 'blue_violet',
 });


  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rangeMode, setRangeMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [planName, setPlanName] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [purchasingKey, setPurchasingKey] = useState('');
  // Fallback (9) matches pricing_plans.payperuse.monthly_price at the time this
  // was written — only shown for the instant before the live fetch below resolves,
  // never used to actually charge anyone (the server re-reads the DB price itself).
  const [payPerReportPrice, setPayPerReportPrice] = useState(9);

  const applyMonth = (monthValue) => {
    setSelectedMonth(monthValue);

    if (!monthValue) {
      setForm((f) => ({ ...f, dateStart: '', dateEnd: '' }));
      return;
    }

    const [year, month] = monthValue.split('-').map(Number);
    const start = new Date(Date.UTC(year, month - 1, 1));
    const end = new Date(Date.UTC(year, month, 0));
    const toISO = (d) => d.toISOString().slice(0, 10);

    setForm((f) => ({ ...f, dateStart: toISO(start), dateEnd: toISO(end) }));
  };

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getSubscription().then((s) => setPlanName(s?.plan_name || 'free')).catch(() => setPlanName('free'));
    getPublicPricing()
      .then((plans) => {
        const payPerReport = plans?.find((p) => p.plan_key === 'payperuse');
        if (payPerReport) setPayPerReportPrice(payPerReport.monthly_price);
      })
      .catch(() => {});
  }, []);

  const loadTemplates = () => {
    if (!planName || planName === 'free') {
      setTemplates([]);
      return;
    }
    getReportTemplates()
      .then((data) => setTemplates(data?.templates || []))
      .catch(() => setTemplates([]));
  };

  useEffect(loadTemplates, [planName]);

  const handlePurchaseTemplate = async (tpl) => {
    try {
      setPurchasingKey(tpl.template_key);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        return;
      }

      const orderData = await purchaseTemplateOrder(tpl.template_key);

      const options = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'Unbrand',
        description: `Unlock ${tpl.display_name} report template`,
        order_id: orderData.order.id,

        handler: async function (response) {
          try {
            await verifyTemplatePurchase(tpl.template_key, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            loadTemplates();
            setForm((f) => ({ ...f, templateKey: tpl.template_key }));
            toast.success(`${tpl.display_name} unlocked!`);
          } catch {
            toast.error('Purchase verification failed');
          }
        },

        theme: { color: '#2563EB' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function () {
        toast.error('Payment failed or cancelled');
      });
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start purchase');
    } finally {
      setPurchasingKey('');
    }
  };

  useEffect(() => {
    if (form.clientId) {
      getReportHistory(form.clientId).then(setHistory).catch(() => {});
    } else {
      setHistory([]);
    }
  }, [form.clientId]);
useEffect(() => {
  if (form.clientId) {
    getSummary(form.clientId)
      .then(setSummary)
      .catch(() => setSummary(null));
  } else {
    setSummary(null);
  }
}, [form.clientId]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  // Pay Per Report: one Razorpay charge per report (amount set server-side
  // from pricing_plans, see createReportCreditOrder), resolved with the
  // paid purchase id that /reports/generate requires as `purchaseId`.
  // Mirrors handlePurchaseTemplate's flow above, but as a promise instead
  // of updating template state, since this gates generateReport() itself.
  const payForReportCredit = () => new Promise(async (resolve, reject) => {
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        reject(new Error('razorpay_load_failed'));
        return;
      }

      const orderData = await createReportCreditOrder();

      const options = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'Unbrand',
        description: 'Pay Per Report — single report credit',
        order_id: orderData.order.id,

        handler: async function (response) {
          try {
            const verified = await verifyReportCreditPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            resolve(verified.purchaseId);
          } catch (err) {
            toast.error('Payment verification failed');
            reject(err);
          }
        },

        theme: { color: '#2563EB' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function () {
        toast.error('Payment failed or cancelled');
        reject(new Error('payment_failed'));
      });
      razorpay.open();
    } catch (err) {
      reject(err);
    }
  });

  const handleGenerate = async () => {
    if (!form.clientId) {
      toast.error('Please select a client');
      return;
    }

    setGenerating(true);
    setGeneratedUrl(null);

    try {
      let purchaseId;
      if (planName === 'payperuse') {
        purchaseId = await payForReportCredit();
      }

      const result = await generateReport({
        clientId: form.clientId,
        dateStart: form.dateStart || undefined,
        dateEnd: form.dateEnd || undefined,
        platform: form.platform !== 'all' ? form.platform : undefined,
        customTitle: form.customTitle || undefined,
        includeComparison: form.includeComparison,
        agencyBranding: true,
        currency: form.currency,
        templateKey: form.templateKey,
        colorTheme: form.colorTheme,
        purchaseId,
      });


      const url = getPdfUrl(result.pdfUrl || result.url);
      setGeneratedUrl(url);

      trackEvent('report_generated', {
        template_key: form.templateKey,
        color_theme: form.colorTheme,
        platform: form.platform,
      });
      toast.success('Report generated!');

      if (form.clientId) {
        getReportHistory(form.clientId).then(setHistory);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };
const handleDeleteReport = async (reportId) => {
  const confirmDelete = window.confirm('Are you sure you want to delete this report?');

  if (!confirmDelete) return;

  try {
    await deleteReport(reportId);

    toast.success('Report deleted successfully');

    if (form.clientId) {
      const updatedHistory = await getReportHistory(form.clientId);
      setHistory(updatedHistory);
    }
  } catch (error) {
      const msg =
        error.response?.data?.error ||
        'Failed to generate report';

      toast.error(msg);
    }
};
  const client = clients.find((c) => String(c.id) === String(form.clientId));

  return (
    <div className="fade-in">
      <div className="page-header">
        <div className="page-title">Generate Reports</div>
        <div className="page-subtitle">Create beautiful PDF reports for your clients</div>
      </div>

      <div className="grid grid-2" style={{ gap: 20, alignItems: 'start' }}>
        <div className="card card-pad">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 20 }}>
            Report Configuration
          </div>
        <div className="form-group">
          <label className="form-label">Client *</label>

          <select
            className="form-select"
            value={form.clientId}
            onChange={set('clientId')}
          >
            <option value="">Select client...</option>

            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
         <div className="form-group">
           <label className="form-label">Currency</label>

           <select
             className="form-select"
             value={form.currency}
             onChange={set('currency')}
           >
             {CURRENCIES.map((c) => (
               <option key={c.code} value={c.code}>
                 {c.code} ({c.symbol})
               </option>
             ))}
           </select>
         </div>

            <div className="form-group">
              <label className="form-label">Custom Report Title</label>
              <input
                className="form-input"
                placeholder={client ? `Performance Report — ${client.name}` : 'Performance Report'}
                value={form.customTitle}
                onChange={set('customTitle')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Platform Filter</label>
              <select className="form-select" value={form.platform} onChange={set('platform')}>
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {p === 'all' ? 'All Platforms' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Report Range</label>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 10,
                  background: 'var(--bg3)',
                  padding: 5,
                  borderRadius: 12,
                  width: 'fit-content',
                }}
              >
                {['month', 'custom'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setRangeMode(mode)}
                    style={{
                      padding: '7px 16px',
                      borderRadius: 9,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                      background: rangeMode === mode ? 'var(--bg2)' : 'transparent',
                      color: rangeMode === mode ? 'var(--text)' : 'var(--text2)',
                      boxShadow: rangeMode === mode ? 'var(--shadow)' : 'none',
                    }}
                  >
                    {mode === 'month' ? 'Month' : 'Custom Range'}
                  </button>
                ))}
              </div>
            </div>

            {rangeMode === 'month' ? (
              <div className="form-group">
                <label className="form-label">Month</label>
                <input
                  type="month"
                  className="form-input"
                  value={selectedMonth}
                  onChange={(e) => applyMonth(e.target.value)}
                  max={new Date().toISOString().slice(0, 7)}
                />
              </div>
            ) : (
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Date From</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.dateStart}
                    onChange={set('dateStart')}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Date To</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.dateEnd}
                    onChange={set('dateEnd')}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="incl-comp"
                checked={form.includeComparison}
                onChange={set('includeComparison')}
                style={{ cursor: 'pointer' }}
              />
              <label
                htmlFor="incl-comp"
                style={{ fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
              >
                Include month-over-month comparison
              </label>
            </div>

            {planName && !['free', 'payperuse'].includes(planName) && templates.length > 0 && (
              <div className="form-group">
                <label className="form-label">Report Format</label>
                <div className="grid grid-3" style={{ gap: 10 }}>
                  {templates.map((tpl) => {
                    const selected = form.templateKey === tpl.template_key;
                    const locked = tpl.access === 'locked_pro' || tpl.access === 'locked_agency';
                    const lockLabel = tpl.access === 'locked_pro' ? 'Pro & Agency' : 'Agency only';
                    const badgeColor = tpl.access === 'locked_agency' || tpl.gating === 'agency'
                      ? { bg: 'rgba(168,85,247,.14)', text: '#7E22CE' }
                      : { bg: 'rgba(37,99,235,.12)', text: '#2563EB' };

                    return (
                      <div
                        key={tpl.template_key}
                        onClick={() => {
                          if (locked || tpl.access === 'locked_premium') return;
                          setForm((f) => ({ ...f, templateKey: tpl.template_key }));
                        }}
                        style={{
                          padding: 12,
                          borderRadius: 10,
                          border: selected ? '1.5px solid #2563EB' : '1px solid var(--border)',
                          background: selected ? '#EFF6FF' : locked ? 'var(--bg3)' : 'var(--bg2)',
                          cursor: locked || tpl.access === 'locked_premium' ? 'default' : 'pointer',
                          opacity: locked ? 0.75 : 1,
                          position: 'relative',
                        }}
                      >
                        {selected && (
                          <div style={{ position: 'absolute', top: 8, right: 8, color: '#2563EB' }}>
                            <Check size={14} />
                          </div>
                        )}
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                          {tpl.display_name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, minHeight: 28 }}>
                          {tpl.description}
                        </div>

                        {locked && (
                          <span style={{
                            fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999,
                            background: badgeColor.bg, color: badgeColor.text, textTransform: 'uppercase',
                          }}>
                            {lockLabel}
                          </span>
                        )}
                        {tpl.access === 'locked_premium' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePurchaseTemplate(tpl);
                            }}
                            disabled={purchasingKey === tpl.template_key}
                            style={{
                              fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999,
                              background: 'rgba(217,119,6,.14)', color: '#B45309',
                              border: 'none', cursor: 'pointer',
                            }}
                          >
                            {purchasingKey === tpl.template_key
                              ? 'Processing...'
                              : `Buy for ₹${Number(tpl.price_inr).toLocaleString('en-IN')}`}
                          </button>
                        )}
                        {tpl.access === 'unlocked_premium' && (
                          <span style={{
                            fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999,
                            background: 'var(--success-light)', color: 'var(--success)',
                          }}>
                            Unlocked
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {templates.find((t) => t.template_key === form.templateKey)?.supports_color_theme && (
                  <div style={{ marginTop: 14 }}>
                    <label className="form-label">Color Theme</label>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {COLOR_THEME_OPTIONS.map((theme) => (
                        <button
                          key={theme.key}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, colorTheme: theme.key }))}
                          title={theme.label}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 10px', borderRadius: 999,
                            border: form.colorTheme === theme.key ? '1.5px solid #2563EB' : '1px solid var(--border)',
                            background: form.colorTheme === theme.key ? '#EFF6FF' : 'var(--bg2)',
                            cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--text)',
                          }}
                        >
                          <span style={{
                            width: 12, height: 12, borderRadius: '50%',
                            background: theme.swatch, flexShrink: 0,
                          }} />
                          {theme.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              className="btn btn-primary btn-lg"
              onClick={handleGenerate}
              disabled={generating || !form.clientId}
              style={{ justifyContent: 'center', marginTop: 4 }}
            >
              {generating ? (
                <>
                  <span
                    className="spin"
                    style={{
                      width: 15,
                      height: 15,
                      border: '2px solid rgba(255,255,255,.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}
                  />{' '}
                  Generating...
                </>
              ) : (
                <>
                  <FileBarChart2 size={15} />
                  {planName === 'payperuse' ? `Pay ₹${payPerReportPrice} & Generate Report` : 'Generate PDF Report'}
                </>
              )}
            </button>
            {planName === 'payperuse' && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                Each report on the Pay Per Report plan costs ₹{payPerReportPrice} and includes an "Unbrand" watermark.
              </div>
            )}

            {generatedUrl && (
              <div
                style={{
                  padding: '14px 16px',
                  background: 'var(--success-light)',
                  border: '1px solid #A7F3D0',
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--success)',
                    marginBottom: 8,
                  }}
                >
                  ✓ Report Generated!
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <a
                    href={generatedUrl}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm"
                    style={{ textDecoration: 'none' }}
                  >
                    <Download size={12} /> Download PDF
                  </a>

                  <a

                    href={generatedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary btn-sm"
                    style={{ textDecoration: 'none' }}
                  >
                    <ExternalLink size={12} /> Preview
                  </a>
                </div>
              </div>
            )}

        {summary && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>
              Live Performance Snapshot
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {[
                {
                  label: form.currency !== 'INR' ? 'Spend (approx.)' : 'Spend',
                  value: `${getCurrencySymbol(form.currency)} ${convertCurrency(
                    summary.spend,
                    form.currency
                  ).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
                },
                summary.has_reach_field
                  ? {
                  label: 'Reach',
                  value: Number(summary.reach || 0).toLocaleString('en-IN'),
                    }
                  : null,
            {
              label: 'Impressions',
              value: Number(summary.impressions || 0).toLocaleString('en-IN'),
            },
            { label: 'Leads', value: Number(summary.conversions || 0).toLocaleString('en-IN') },
              ].filter(Boolean).map((item, i) => (
                <div
                  key={i}
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg,#F8FAFC,#EEF2FF)',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800 }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
         <div
           className="card card-pad"
           style={{
             background: 'linear-gradient(135deg,#EEF2FF,#F8FAFC)',
             border: '1px solid #C7D2FE',
             boxShadow: '0 18px 35px rgba(15, 23, 42, 0.08)',
           }}
         >

           <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
             <div
               style={{
                 width: 38,
                 height: 38,
                 borderRadius: 12,
                 background: 'linear-gradient(135deg,#2563EB,#7C3AED)',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
               }}
             >
               <FileBarChart2 size={18} color="#fff" />
             </div>
             <div>
               <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
                 Premium PDF Report
               </div>
               <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                 Visual, clean and mentor-ready format
               </div>
             </div>
           </div>

           {[
             { title: 'Executive Summary', desc: 'Spend, reach, impressions, leads and cost per lead' },
             { title: 'Visual Charts', desc: 'Top campaign spend bar chart inside PDF' },
             { title: 'Campaign Table', desc: 'Campaign-wise performance breakdown' },
             { title: 'Insights', desc: 'Auto-written observations and recommendations' },
             { title: 'Branding', desc: 'Agency name, colors and professional cover page' },
           ].map((item, i) => (
             <div
               key={i}
               style={{
                 display: 'flex',
                 gap: 10,
                 padding: '10px 0',
                 borderTop: i === 0 ? 'none' : '1px solid rgba(99,102,241,.15)',
               }}
             >
               <div
                 style={{
                   width: 22,
                   height: 22,
                   borderRadius: '50%',
                   background: '#DCFCE7',
                   color: '#15803D',
                   fontSize: 12,
                   fontWeight: 800,
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   flexShrink: 0,
                 }}
               >
                 ✓
               </div>
               <div>
                 <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                   {item.title}
                 </div>
                 <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                   {item.desc}
                 </div>
               </div>
             </div>
           ))}
         </div>
          <div className="card">
            <div
              className="card-pad"
              style={{
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Clock size={14} style={{ color: 'var(--text3)' }} />
              <div style={{ fontWeight: 700, fontSize: 14 }}>Report History</div>
            </div>

            {!form.clientId ? (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                Select a client to see history
              </div>
            ) : history.length === 0 ? (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                No reports generated yet
              </div>
            ) : (
              history.map((r) => (
                <div
                  key={r.id}
                  style={{
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'var(--primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FileBarChart2 size={14} color="var(--primary)" />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {new Date(r.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </div>

                 {r.file_path && r.file_available !== false ? (
                     <a
                       href={getPdfUrl(r.pdf_url || r.file_path)}
                       target="_blank"
                       rel="noreferrer"
                       className="btn btn-ghost btn-sm"
                       style={{ color: 'var(--primary)' }}
                     >
                       <Download size={12} />
                     </a>
                   ) : r.file_path ? (
                     <button
                       type="button"
                       className="btn btn-ghost btn-sm"
                       style={{ color: 'var(--text3)' }}
                       title="Report file is no longer available. Generate the report again."
                       onClick={() => toast.error('Report file is no longer available. Generate the report again.')}
                     >
                       <Download size={12} />
                     </button>
                   ) : null}

                   <button
                     type="button"
                     onClick={() => handleDeleteReport(r.id)}
                     className="btn btn-ghost btn-sm"
                     style={{ color: '#DC2626' }}
                     title="Delete report"
                   >
                     Delete
                   </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

  );
}
